// src/useForkRef.ts
import { useMemo } from "react";
var setRef = (ref, value) => {
  if (typeof ref === "function") {
    ref(value);
  } else if (ref) {
    ref.current = value;
  }
};
var useForkRef = (refA, refB) => {
  return useMemo(() => {
    if (!refA && !refB) {
      return null;
    }
    return (refValue) => {
      refA && setRef(refA, refValue);
      refB && setRef(refB, refValue);
    };
  }, [refA, refB]);
};

// src/usePrevious.ts
import { useEffect, useRef } from "react";
var usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

// src/useForceUpdate.ts
import { useCallback, useState } from "react";
var useForceUpdate = () => {
  const [, setTick] = useState(0);
  const update = useCallback(() => {
    setTick((tick) => tick + 1);
  }, []);
  return update;
};

// src/useMediaQuery.ts
import { useCallback as useCallback2, useEffect as useEffect2, useMemo as useMemo2, useState as useState2 } from "react";
var useMediaQuery = (queries, defaultValue) => {
  const { keys, values } = useMemo2(
    () => ({ values: Object.values(queries), keys: Object.keys(queries) }),
    [queries]
  );
  const match = useCallback2(
    () => values[keys.findIndex((q) => matchMedia(q).matches)] ?? defaultValue,
    [defaultValue, keys, values]
  );
  const [value, set] = useState2(match);
  useEffect2(() => {
    const handler = () => set(match);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [match]);
  return value;
};

// src/useReduxAction.ts
import { useSelector } from "react-redux";

// src/useReduxActionDispatcher.ts
import { useMemo as useMemo3 } from "react";
import { useDispatch } from "react-redux";
import {
  bindActionCreators
} from "@reduxjs/toolkit";
function useActionDispatcher(actionCreator) {
  const dispatch = useDispatch();
  return useMemo3(() => bindActionCreators(actionCreator, dispatch), [actionCreator, dispatch]);
}
var useReduxActionDispatcher_default = useActionDispatcher;

// src/useReduxAction.ts
function useAction(selector, actionCreator) {
  const state = useSelector(selector);
  const dispatch = useReduxActionDispatcher_default(actionCreator);
  return [state, dispatch];
}

// src/useIntersectionObserver.ts
import { useEffect as useEffect3, useRef as useRef2, useState as useState3 } from "react";
var useIntersectionObserver = ({
  threshold = 0,
  root = null,
  rootMargin = "0%",
  freezeOnceVisible = false
}) => {
  const elementRef = useRef2(null);
  const [entry, setEntry] = useState3();
  const frozen = entry?.isIntersecting && freezeOnceVisible;
  const updateEntry = ([entry2]) => {
    setEntry(entry2);
  };
  useEffect3(() => {
    const node = elementRef?.current;
    const hasIOSupport = !!window.IntersectionObserver;
    if (!hasIOSupport || frozen || !node)
      return;
    const observerParams = { threshold, root, rootMargin };
    const observer = new IntersectionObserver(updateEntry, observerParams);
    observer.observe(node);
    return () => observer.disconnect();
  }, [elementRef?.current, JSON.stringify(threshold), root, rootMargin, frozen]);
  return [elementRef, entry];
};

// src/useReducer.ts
import { useCallback as useCallback3, useRef as useRef3, useState as useState4 } from "react";
var useReducer = (reducer, initialState, middlewares = []) => {
  const [state, setState] = useState4(initialState);
  const stateRef = useRef3(state);
  const dispatchRef = useRef3(() => {
    throw new Error(
      `Dispatching while constructing your middleware is not allowed. Other middleware would not be applied to this dispatch.`
    );
  });
  const middlewareAPI = {
    getState: () => stateRef.current,
    dispatch: (action) => dispatchRef.current(action)
  };
  const localDispatch = (action) => {
    stateRef.current = reducer(stateRef.current, action);
    setState(stateRef.current);
  };
  dispatchRef.current = middlewares.map((middleware) => middleware(middlewareAPI)).reduceRight((acc, middleware) => middleware(acc), localDispatch);
  const dispatch = useCallback3((action) => dispatchRef.current(action), []);
  return [state, dispatch];
};

// src/useMeasure.ts
import { useMeasure } from "react-use";
import { useMeasure as useMeasure2 } from "react-use";

// src/useDelayedLoader.ts
import { useEffect as useEffect4, useRef as useRef4, useState as useState5 } from "react";
var useDelayedLoader = (loading, delay = 1e3) => {
  const [hasChanged, setHasChanged] = useState5(false);
  const prevLoadingRef = useRef4();
  useEffect4(() => {
    let timeoutId;
    if (prevLoadingRef.current !== loading) {
      timeoutId = setTimeout(() => {
        setHasChanged(true);
      }, delay);
    } else {
      setHasChanged(false);
    }
    prevLoadingRef.current = loading;
    return () => clearTimeout(timeoutId);
  }, [loading, delay]);
  return !hasChanged;
};

// src/useEventCallback.ts
import { useRef as useRef5 } from "react";
function useEventCallback(fn) {
  const ref = useRef5(fn);
  ref.current = fn;
  return useRef5(
    (...args) => (
      // @ts-expect-error hide `this`
      // tslint:disable-next-line:ban-comma-operator
      (0, ref.current)(...args)
    )
  ).current;
}

// src/useIsomorphicEffect.ts
import { useEffect as useEffect5, useLayoutEffect } from "react";
var useIsomorphicEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect5;
export {
  setRef,
  useAction,
  useActionDispatcher,
  useDelayedLoader,
  useEventCallback,
  useForceUpdate,
  useForkRef,
  useIntersectionObserver,
  useIsomorphicEffect,
  useMeasure,
  useMediaQuery,
  usePrevious,
  useReducer
};
